import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Team', href: '/team' },
      { name: 'Careers', href: '/careers' },
      { name: 'Press', href: '/press' },
    ],
    services: [
      { name: 'Find Properties', href: '/property-search-browse' },
      { name: 'Houses for Rent', href: '/for-rent' },
      { name: 'Property for Sale', href: '/for-sale' },
      { name: 'Land for Sale', href: '/land' },
      { name: 'List Property', href: '/list-property' },
      { name: 'Notice Board', href: '/notice-board' },
    ],
    professionals: [
      { name: 'Real Estate Agent Login', href: '/pro/login' },
      { name: 'Join as Agent / Broker / Advisor', href: '/pro/register' },
      { name: 'Agent Dashboard', href: '/agent-dashboard' },
      { name: 'Collaboration Workspace', href: '/workspace' },
      { name: 'Get Verified', href: '/agent-dashboard/verification' },
      { name: 'Respond to Needs', href: '/notice-board' },
    ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'How hunting fees work', href: '/how-fees-work' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
    ],
    resources: [
      { name: 'Property Guides', href: '/guides' },
      { name: 'Market Reports', href: '/reports' },
      { name: 'Investment Tips', href: '/tips' },
      { name: 'Blog', href: '/blog' },
    ],
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', href: 'https://facebook.com' },
    { name: 'Twitter', icon: 'Twitter', href: 'https://twitter.com' },
    { name: 'Instagram', icon: 'Instagram', href: 'https://instagram.com' },
    { name: 'LinkedIn', icon: 'Linkedin', href: 'https://linkedin.com' },
  ];

  return (
    <footer className="bg-card border-t border-border">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/property-search-browse" className="flex items-center space-x-2 mb-4">
              <img src="/assets/images/homelogo.png" alt="HireBroker Logo" className="w-18 h-10" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-md">
              Verified houses, apartments, and land for sale or rent in Kampala, Wakiso,
              Entebbe and across Uganda. HireBroker connects you with credential-checked agents.
            </p>

            {/* Newsletter Signup */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Stay Updated</h4>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
                />
                <Button size="sm" className="whitespace-nowrap">
                  Subscribe
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Get the latest property listings and market insights delivered to your inbox.
              </p>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Real Estate Agents */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">For Real Estate Agents</h3>
            <ul className="space-y-3">
              {footerLinks.professionals.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-4">Popular areas</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {[
              ['Kampala rentals', '/for-rent/kampala'],
              ['Kampala for sale', '/for-sale/kampala'],
              ['Wakiso land', '/land/wakiso'],
              ['Entebbe rentals', '/for-rent/entebbe'],
              ['Ntinda for sale', '/for-sale/ntinda'],
              ['Kira rentals', '/for-rent/kira'],
              ['Mukono land', '/land/mukono'],
              ['All listings', '/property-search-browse'],
              ['HTML sitemap', '/sitemap'],
            ].map(([label, href]) => (
              <Link
                key={href}
                to={href}
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>

        {/* Contact Info & Mobile Apps */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8">
            {/* Contact Information */}
            <div className="flex flex-col sm:flex-row items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={16} />
                <span>+256 774 509 859</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} />
                <span>info@hirebroker.co</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>Kampala, Uganda</span>
              </div>
            </div>

            {/* Mobile Apps & Social Links */}
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Mobile App Downloads */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-foreground mr-2">Download App:</span>
                <a
                  href="https://play.google.com/store/apps/details?id=com.hirebroker.app&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform duration-200 hover:scale-105"
                  aria-label="Download on Google Play"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>
                <a
                  href="https://apps.apple.com/ug/app/hire-broker/id6476240355"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform duration-200 hover:scale-105"
                  aria-label="Download on App Store"
                >
                  <img
                    src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-9 w-auto"
                  />
                </a>
              </div>

              {/* Social Media Links */}
              <div className="flex items-center gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-muted hover:bg-primary hover:text-primary-foreground rounded-full flex items-center justify-center transition-colors duration-200"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={18} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-muted/50 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground">
              © {currentYear} HireBroker. All rights reserved.
            </div>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy" className="hover:text-primary transition-colors duration-200">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-colors duration-200">
                Terms
              </Link>
              <Link to="/cookies" className="hover:text-primary transition-colors duration-200">
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
